import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { isEmpty, last, omit, some } from 'lodash';
import { gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import {
    getPath,
    matchPathWithoutQuery,
    parseQueryParams,
    queryParamHookFactory,
    safeDeserializeDrawerStack,
    stringifyQueryParams,
} from 'modules/taboola-common-frontend-modules/query-params';
import { PERSISTENCE_TYPE } from 'modules/taboola-common-frontend-modules/storage/persistenceType';
import { FORM_MODES } from '../../config/formModes';
import { BASE_FORM_ROUTE_PATH } from '../../config/routes';
import { PATH_TO_FORM_MODE_MAP } from '../../modules/campaigns/config/routes/mode';
import { REPORT_FORCE_FETCH_FLAG } from '../../modules/campaigns/modules/campaigns-reports/hooks/useReportForceFetchFlag';
import { useMultiLayerDrawerContext } from '../../modules/taboola-common-frontend-modules/multi-layer-drawer/components/MultiLayerDrawerProvider/MultiLayerDrawerProvider';
import { useCurrentValueGetter } from '../useCurrentValueGetter';
import { useHistoryNavigate } from '../useHistoryNavigate';

export const DRAWER_STACK = 'drawerStack';

const emptyDrawerStack = [];

const useDrawerStackUrl = queryParamHookFactory(DRAWER_STACK, {
    persist: PERSISTENCE_TYPE.MEMORY,
    defaultValue: emptyDrawerStack,
    deserializer: safeDeserializeDrawerStack,
    serializer: value => encodeURIComponent(JSON.stringify(value)),
});

export const useDrawerStack = () => {
    const [drawerStack] = useDrawerStackUrl();
    const { setEntityData } = useMultiLayerDrawerContext();

    const location = useLocation();
    const getLocation = useCurrentValueGetter(location);
    const getDrawerStack = useCurrentValueGetter(drawerStack);
    const navigate = useHistoryNavigate();

    const navigateToTopLayerPath = useCallback(
        ({ stack, forceFetchFlag = false, module }) => {
            const lastLayer = last(stack)?.path || `/${module}`;

            if (getLocation().pathname === lastLayer && !isEmpty(getDrawerStack())) {
                return;
            }

            const [lastLayerPath, lastLayerRawQuery = ''] = lastLayer.split('?');
            const currentParams = parseQueryParams(getLocation().search);
            const lastParams = parseQueryParams(lastLayerRawQuery ? `?${lastLayerRawQuery}` : '');

            const merged = {
                ...omit(currentParams, DRAWER_STACK),
                ...omit(lastParams, DRAWER_STACK),
                [DRAWER_STACK]: stack,
                ...(forceFetchFlag && { [REPORT_FORCE_FETCH_FLAG]: Date.now() }),
            };

            const newSearch = stringifyQueryParams(merged);
            navigate(`${lastLayerPath}${newSearch}`);
        },
        [getLocation, navigate, getDrawerStack]
    );

    const push = useCallback(
        form => {
            const currentStack = getDrawerStack();
            if (some(currentStack, ({ path }) => getPath(path) === getPath(form.path))) {
                gtmTracker.trackError(`${form.id} form already exists in drawer stack`);
                return;
            }

            navigateToTopLayerPath({
                stack: [...currentStack, form],
            });
        },
        [getDrawerStack, navigateToTopLayerPath]
    );

    const pop = useCallback(
        (data, path = null) => {
            const currentStack = getDrawerStack();
            const newStack = [...currentStack];
            const form = newStack.pop();

            if (path) {
                newStack.push({ path });
            }

            if (data) {
                setEntityData(data);
            }

            const { mode, module } =
                matchPathWithoutQuery(form?.path, {
                    path: BASE_FORM_ROUTE_PATH,
                    exact: false,
                })?.params || {};

            const forceFetchFlag = data && isEmpty(newStack) && PATH_TO_FORM_MODE_MAP[mode] === FORM_MODES.CREATE;

            navigateToTopLayerPath({ stack: newStack, forceFetchFlag, module });
        },
        [getDrawerStack, setEntityData, navigateToTopLayerPath]
    );

    const replace = useCallback(
        form => {
            const currentStack = getDrawerStack();
            if (isEmpty(currentStack)) {
                gtmTracker.trackError('Cannot replace item in empty drawer stack');
                return;
            }

            const newStack = [...currentStack];
            newStack.pop();
            newStack.push(form);

            navigateToTopLayerPath({
                stack: newStack,
            });
        },
        [getDrawerStack, navigateToTopLayerPath]
    );

    const getPreviousLayer = useCallback(() => getDrawerStack().at(-2), [getDrawerStack]);

    const isMultiLayerMode = useMemo(() => drawerStack.length > 1, [drawerStack]);

    return { stack: drawerStack, push, pop, replace, isMultiLayerMode, peekPrevious: getPreviousLayer };
};
